import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Checkbox,
  Avatar,
} from '@mui/material';
import {
  Comment as CommentIcon,
  CheckCircle as ApproveIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Flag as ReportIcon,
  SelectAll as SelectAllIcon,
  DoneAll as ApproveAllIcon,
  DeleteSweep as DeleteAllIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as articleService from '../services/articleService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import { ConfirmDialog } from '../components/common';

const ArticleCommentModeration: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Tab
  const [activeTab, setActiveTab] = useState(0);

  // Data state
  const [comments, setComments] = useState<articleService.ArticleComment[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null); // single id or 'bulk'
  const [submitting, setSubmitting] = useState(false);

  // Load comments
  const loadComments = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      let response: articleService.CommentsResponse;
      if (activeTab === 0) {
        response = await articleService.getPendingComments(page + 1, rowsPerPage);
      } else {
        response = await articleService.getReportedComments(page + 1, rowsPerPage);
      }
      setComments(response.docs || []);
      setTotalDocs(response.totalDocs || 0);
    } catch (err: any) {
      setError(err.message || 'خطا در بارگذاری نظرات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, rowsPerPage]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
  };

  // ==================== SELECTION ====================

  const handleSelectAll = () => {
    if (selectedIds.size === comments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(comments.map((c) => c._id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  // ==================== SINGLE ACTIONS ====================

  const handleApproveComment = async (id: string) => {
    try {
      await articleService.approveComment(id);
      enqueueSnackbar('نظر تایید شد', { variant: 'success' });
      loadComments();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تایید نظر', { variant: 'error' });
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteTarget(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      if (deleteTarget === 'bulk') {
        // Bulk delete
        const promises = Array.from(selectedIds).map((id) => articleService.deleteComment(id));
        await Promise.all(promises);
        enqueueSnackbar(`${selectedIds.size} نظر حذف شد`, { variant: 'success' });
        setSelectedIds(new Set());
      } else {
        await articleService.deleteComment(deleteTarget);
        enqueueSnackbar('نظر حذف شد', { variant: 'success' });
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadComments();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در حذف نظر', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== BULK ACTIONS ====================

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    try {
      const promises = Array.from(selectedIds).map((id) => articleService.approveComment(id));
      await Promise.all(promises);
      enqueueSnackbar(`${selectedIds.size} نظر تایید شد`, { variant: 'success' });
      setSelectedIds(new Set());
      loadComments();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تایید نظرات', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.size === 0) return;
    setDeleteTarget('bulk');
    setDeleteDialogOpen(true);
  };

  // ==================== FORMAT ====================

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR');
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // ==================== RENDER ====================

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <MyShiftStatusBanner category="article_moderation" />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <CommentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          مدیریت نظرات مقالات
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadComments} disabled={loading}>
          بروزرسانی
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="در انتظار تایید" />
          <Tab label="گزارش‌شده" />
        </Tabs>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1.5,
              bgcolor: 'primary.50',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {selectedIds.size} نظر انتخاب شده
            </Typography>
            {activeTab === 0 && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={submitting ? <CircularProgress size={16} /> : <ApproveAllIcon />}
                onClick={handleBulkApprove}
                disabled={submitting}
              >
                تایید همه
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteAllIcon />}
              onClick={handleBulkDeleteClick}
              disabled={submitting}
            >
              حذف همه
            </Button>
          </Box>
        )}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.size > 0 && selectedIds.size < comments.length}
                    checked={comments.length > 0 && selectedIds.size === comments.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>کاربر</TableCell>
                <TableCell>محتوای نظر</TableCell>
                <TableCell>مقاله</TableCell>
                <TableCell>تاریخ</TableCell>
                {activeTab === 1 && <TableCell align="center">تعداد گزارش</TableCell>}
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={activeTab === 1 ? 7 : 6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : comments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeTab === 1 ? 7 : 6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {activeTab === 0 ? 'نظر در انتظار تاییدی وجود ندارد' : 'نظر گزارش‌شده‌ای وجود ندارد'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                comments.map((comment) => (
                  <TableRow key={comment._id} hover selected={selectedIds.has(comment._id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(comment._id)}
                        onChange={() => handleSelectOne(comment._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={comment.userId?.profileImage}
                          sx={{ width: 32, height: 32 }}
                        >
                          {comment.userId?.name?.charAt(0) || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {comment.userId?.name || 'نامشخص'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {comment.userId?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip title={comment.content}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {truncateText(comment.content)}
                        </Typography>
                      </Tooltip>
                      {comment.isEdited && (
                        <Chip label="ویرایش شده" size="small" variant="outlined" sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {comment.articleId?.title || 'نامشخص'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(comment.createdAt)}</Typography>
                    </TableCell>
                    {activeTab === 1 && (
                      <TableCell align="center">
                        <Chip
                          icon={<ReportIcon />}
                          label={comment.reportCount || 0}
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {(activeTab === 0 || !comment.isApproved) && (
                          <Tooltip title="تایید">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleApproveComment(comment._id)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="حذف">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(comment._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalDocs}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} از ${count}`}
        />
      </Paper>

      {/* ==================== DELETE CONFIRMATION DIALOG ==================== */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="حذف نظر"
        message={
          deleteTarget === 'bulk'
            ? `آیا از حذف ${selectedIds.size} نظر انتخاب شده اطمینان دارید؟`
            : 'آیا از حذف این نظر اطمینان دارید؟'
        }
        confirmText="حذف"
        cancelText="انصراف"
        confirmColor="error"
        loading={submitting}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
      >
        <Alert severity="warning">
          این عملیات قابل بازگشت نیست.
        </Alert>
      </ConfirmDialog>
    </Container>
  );
};

export default ArticleCommentModeration;
